import { useEffect, useRef } from 'react';
import { useSignalR } from 'contexts/SignalRContext';
import { useUserInfo } from 'hooks/useUserInfo';
import { getAppSource } from 'utils/shellDetection';
import appConfig from 'appConfig';
import useApi from 'api/api';

/**
 * Hook to automatically respond to version info requests from admin
 * 
 * When admin requests version info via SignalR, this hook will:
 * 1. Listen for VersionInfoRequest
 * 2. Respond with current user's version info
 * 
 * This enables real-time version tracking across all connected clients
 */
export function useVersionInfoReporter() {
  const { connection } = useSignalR();
  const { userName } = useUserInfo();
  const { post } = useApi();
  const handlerRegistered = useRef(false);
  const baseUrl = appConfig.api.orderApiUrl;

  useEffect(() => {
    if (connection && userName && !handlerRegistered.current) {
      console.log('[VersionInfoReporter] Registering version info handler');

      const handleVersionInfoRequest = async () => {
        try {
          console.log('[VersionInfoReporter] Received version info request, responding...');
          
          const versionInfo = {
            username: userName,
            version: appConfig.version,
            appSource: getAppSource(),
            timestamp: new Date().toISOString(),
            connectionId: connection.connectionId || 'unknown'
          };

          console.log('[VersionInfoReporter] Broadcasting version info:', versionInfo);

          // Send to API endpoint, which will broadcast via _hubContext (isolated from order messages)
          const response = await post(`${baseUrl}/admin/version/report-info`, versionInfo);
          
          if (response && response.status === 'success') {
            console.log('[VersionInfoReporter] Version info sent successfully');
          } else {
            console.error('[VersionInfoReporter] Failed to report version info:', response);
          }
        } catch (error) {
          console.error('[VersionInfoReporter] Failed to report version info:', error);
        }
      };

      // Register handler
      connection.on('VersionInfoRequest', handleVersionInfoRequest);
      handlerRegistered.current = true;

      console.log('[VersionInfoReporter] Handler registered successfully');

      // Cleanup
      return () => {
        if (connection) {
          console.log('[VersionInfoReporter] Unregistering version info handler');
          connection.off('VersionInfoRequest', handleVersionInfoRequest);
          handlerRegistered.current = false;
        }
      };
    }
  }, [connection, userName]);
}

